// packages/shared/src/utils/cookieNames.ts
//
// Pure rule for the auth cookies' NAMES — the companion to
// `utils/cookiePolicy.ts`, which owns their security ATTRIBUTES.
//
// **Import-free by design.** The backend pulls this in by relative path
// (`utils/cookieAuth.ts`), the web apps and the Electron main process by their
// own routes, and `tests/unit` compiles under the backend tsconfig with no
// `@showliner/shared` path mapping. A single import here would break at least
// one of those. Same posture as `contrastTone.ts` and `bookingStatus.ts`.
//
// ---------------------------------------------------------------------------
// Why the name has to vary at all
// ---------------------------------------------------------------------------
//
// Production sets `COOKIE_DOMAIN=.showliner.io` (fly.toml) and dev sets
// `COOKIE_DOMAIN=.dev.showliner.io` (fly.dev.toml). The first is a strict
// SUPERSET of the second: `api.dev.showliner.io` matches BOTH, so a browser
// holding a session for each sends both cookies in one header.
//
// When two cookies share a name the server reads the FIRST and silently
// discards the rest (`cookie`'s parser keeps the first occurrence; the
// frontend's `document.cookie` scan in `apiClient.ts` does the same). Per RFC
// 6265 both cookies have `Path=/`, so ordering falls to creation time —
// earliest first — and replacing a cookie PRESERVES its original creation
// time. So whichever environment was signed into first wins permanently.
//
// The symptom is vicious: logging in SUCCEEDS (login writes a cookie rather
// than reading one) and every request afterwards 401s, including the Socket.IO
// handshake, because the fresh cookie lands in second place where nothing ever
// reads it. Signing in again cannot clear it. The backend looks healthy from
// every angle, because it is.
//
// Distinct names remove the collision outright: `showliner_session` and
// `showliner_session_dev` are different cookies and never contend.
//
// ---------------------------------------------------------------------------
// Why production keeps the bare name
// ---------------------------------------------------------------------------
//
// Renaming a cookie invalidates every session using it. Production is the one
// environment where that means signing out real users mid-show for no benefit
// — prod is never the victim here, since a `.dev.showliner.io` cookie is not
// sent to `api.showliner.io`. The breakage only ever runs one way, so only the
// environments that can be shadowed are renamed.

/** The production names. Every other environment appends a suffix to these. */
export const BASE_SESSION_COOKIE_NAME = 'showliner_session';
export const BASE_CSRF_COOKIE_NAME = 'showliner_csrf';

/**
 * Host labels that mark a deployment as living UNDER the production cookie
 * domain, and therefore needing its own cookie names.
 *
 * A list rather than a hardcoded `'dev'` so that standing up the next
 * environment is a one-line change here instead of a rediscovery of this whole
 * bug. `staging` is listed pre-emptively and matches nothing today.
 *
 * Matching is on a whole DNS label, never a substring, so a future
 * `devon.showliner.io` is production and stays on the bare name.
 */
export const SCOPED_ENVIRONMENT_LABELS: readonly string[] = ['dev', 'staging'];

/**
 * Reduce a cookie domain (`.dev.showliner.io`), an origin
 * (`https://api.dev.showliner.io`), or a bare host to a comparable hostname.
 *
 * Hand-rolled rather than using `URL` because the inputs are deliberately
 * heterogeneous — a leading-dot cookie domain is not a valid URL, and `URL`
 * throws rather than degrading. Returns '' for anything unparseable, which
 * callers read as "production / no suffix".
 */
function hostOf(input: string): string {
    let s = (input ?? '').trim().toLowerCase();
    if (!s) return '';

    s = s.replace(/^[a-z][a-z0-9+.-]*:\/\//, ''); // scheme
    const at = s.indexOf('@');
    if (at !== -1) s = s.slice(at + 1); // credentials
    s = s.split('/')[0].split('?')[0].split('#')[0]; // path/query/fragment

    const colon = s.lastIndexOf(':'); // port
    if (colon !== -1 && /^\d+$/.test(s.slice(colon + 1))) s = s.slice(0, colon);

    return s.replace(/^\.+/, ''); // leading dot of a cookie domain
}

/**
 * The suffix this deployment's cookies carry — `''` for production and
 * localhost, `'_dev'` on the dev deployment.
 *
 * Accepts whichever identifier the caller actually has: the server knows its
 * `COOKIE_DOMAIN`, the web apps know their API origin, and both describe the
 * same deployment, so both land on the same answer.
 */
export function environmentCookieSuffix(hostOrUrl: string): string {
    const labels = hostOf(hostOrUrl).split('.').filter(Boolean);
    if (labels.length === 0) return '';

    for (const label of SCOPED_ENVIRONMENT_LABELS) {
        if (labels.includes(label)) return `_${label}`;
    }
    return '';
}

/** Name of the HttpOnly session cookie for the deployment `hostOrUrl` describes. */
export function sessionCookieName(hostOrUrl: string): string {
    return `${BASE_SESSION_COOKIE_NAME}${environmentCookieSuffix(hostOrUrl)}`;
}

/** Name of the (deliberately readable) CSRF cookie for that same deployment. */
export function csrfCookieName(hostOrUrl: string): string {
    return `${BASE_CSRF_COOKIE_NAME}${environmentCookieSuffix(hostOrUrl)}`;
}
